import React, { useEffect } from "react";
import { SaleOrder } from "@/types/general";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import GeneralSection from "./parts/GeneralSection";
import ReferencesSection from "./parts/ReferencesSection";
import AddressSection from "./parts/AddressSection";
import ProductSection from "./parts/ProductSection";
import dayjs from "dayjs";
import { LineGroupUnion, SaleOrdersFormTyping, saleOrdersScheme } from "./types";
import LanguageSection from "./parts/LanguageSection";
import { cn, normalizePrice } from "@helpers/utils";
import UIButton from "@reusables/UIButton";
import { ReactComponent as StartPickingSVG } from "@assets/icons/ic_start_picking.svg";
import BaseSwitch from "@reusables/BaseSwitch";
import { useTranslation } from "react-i18next";

interface MutationLayoutProperties {
    saleOrder: SaleOrder.Extended | undefined;
    onSubmit: (args: SaleOrdersFormTyping & { saveAndPick: boolean }) => void;
    disabled?: boolean;
}

export default function MutationLayout(props: MutationLayoutProperties) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders" });

    const form = useForm<SaleOrdersFormTyping>({
        resolver: zodResolver(saleOrdersScheme),
        defaultValues: {
            mode: "creation",
            is_editable: true,
            is_billing_for_delivery: false,
            order_date: dayjs(),
            lines: [],
            is_invoiced: false,
            deleted_lines: []
        }
    });

    // Add state for saveAndPick
    const [saveAndPick, setSaveAndPick] = React.useState(() => {
        const stored = localStorage.getItem(`saveAndPick.${props.saleOrder ? "editing" : "creation"}`);
        return stored ? JSON.parse(stored) : false;
    });

    React.useEffect(() => {
        localStorage.setItem(`saveAndPick.${props.saleOrder ? "editing" : "creation"}`, JSON.stringify(saveAndPick));
    }, [saveAndPick]);

    useEffect(() => {
        if (props.saleOrder) {
            form.setValue("mode", "editing");
            form.setValue("is_editable", props.saleOrder.is_editable);

            form.setValue("customer", props.saleOrder.customer);
            form.setValue("order_date", props.saleOrder.order_date);
            form.setValue("preferred_delivery_date", props.saleOrder.preferred_delivery_date);
            form.setValue("our_reference", props.saleOrder.our_reference);
            form.setValue("their_reference", props.saleOrder.their_reference);
            form.setValue("payment_terms", props.saleOrder.payment_terms);
            form.setValue("delivery_terms", props.saleOrder.delivery_terms);
            form.setValue("language", props.saleOrder.language);
            if (props.saleOrder.billing_address)
                form.setValue("billing_address", {
                    name: props.saleOrder.billing_address.name,
                    street: props.saleOrder.billing_address.street,
                    city: props.saleOrder.billing_address.city,
                    country: props.saleOrder.billing_address.country
                });
            form.setValue("delivery_address", props.saleOrder?.delivery_address);

            form.setValue("is_billing_for_delivery", props.saleOrder.is_billing_for_delivery);

            const combinedLines: LineGroupUnion[] = props.saleOrder.lines.map((line, index) => {
                if (line.type === "line") {
                    return {
                        ...line.data,
                        unit_price: normalizePrice(line.data.unit_price)
                    };
                } else {
                    return {
                        id: line.data.id,
                        name: line.data.name,
                        comment: line.data.comment,
                        module: line.data.module,
                        module_entity_id: line.data.module_entity_id,
                        lines: line.data.lines.map(groupLine => ({
                            ...groupLine.data,
                            unit_price: normalizePrice(groupLine.data.unit_price)
                        }))
                    };
                }
            });

            form.setValue("lines", combinedLines);

            form.setValue("deleted_lines", []);
        }
    }, [props.saleOrder]);

    const onSubmit = form.handleSubmit((data: SaleOrdersFormTyping) => {
        // Unused, because we forbid editing SO if it's invoiced
        // if (props.saleOrder?.is_invoiced) {
        //     for (let i = 0; i < form.getValues("lines").length; i++) {
        //         const originalQuantity = props.saleOrder?.lines[i].data?.quantity;
        //         const newQuantity = form.getValues("lines")[i].data!.quantity;

        //         // If the new quantity is less than the original, show error and stop the mutation
        //         if (newQuantity < originalQuantity) {
        //             toast.error(`Quantity for product ${form.getValues("lines")[i].data?.product.name} cannot be less than the original quantity.`);
        //             return;
        //         }
        //     }
        // }

        props.onSubmit({ ...data, saveAndPick });
    }, console.error);

    return (
        <FormProvider {...form}>
            <div className={cn("space-y-[40px]", props.disabled && "hidden")}>
                <GeneralSection />
                <ReferencesSection />
                <AddressSection />
                <LanguageSection />
                <ProductSection />
                {/* Switch for Save & Pick */}
                <div
                    className="flex items-center justify-between space-x-2 border border-solid border-gray-300 rounded-md p-2 mt-4 hover:bg-gray-300/10 cursor-pointer transition-[.150s] user-select-none"
                    onClick={() => setSaveAndPick(!saveAndPick)}
                >
                    <div className={cn("flex items-center space-x-2", saveAndPick ? "text-accent" : "text-secondary")}>
                        <StartPickingSVG className="w-4 h-4 text-inherit" />
                        <span className={cn("text-sm font-normal text-inherit")}>{t("general.saveAndPick.label")}</span>
                    </div>
                    <BaseSwitch
                        checked={saveAndPick}
                        aria-label={"Save and Pick switch"}
                    />
                </div>
                <div className="flex justify-center space-x-2">
                    <UIButton
                        onClick={() => onSubmit()}
                        size={"lg"}
                        className="min-w-[200px]"
                    >
                        <span>{t("general.buttons.save")}</span>
                    </UIButton>
                </div>
            </div>
        </FormProvider>
    );
}